import type { MouseEvent } from 'react';
import { SlotInlineEditor } from './SlotInlineEditor';
import { periodOf, staffAssignedInPeriod } from './shiftConstants';
import { isOnLeave } from './leave';
import type { Holiday, Shift, Staff } from './types';

// The slot being edited — the cell's (date, slot), its current occupant, and the
// anchor rect the popover positions against.
export interface EditingSlot {
  date: string;
  slot: number;
  currentStaffId: string | null;
  rect: DOMRect;
}

// Build the editing descriptor from a click on a slot/add affordance.
export function editingFromEvent(
  e: MouseEvent,
  date: string,
  slot: number,
  currentStaffId: string | null,
): EditingSlot {
  e.stopPropagation();
  return { date, slot, currentStaffId, rect: (e.currentTarget as HTMLElement).getBoundingClientRect() };
}

interface SlotEditorPortalProps {
  editing: EditingSlot | null;
  staff: Staff[];
  shifts: Shift[];
  holidays: Holiday[];
  onAssign: (date: string, slot: number, staffId: string) => void;
  onRemove: (date: string, slot: number) => void;
  onClose: () => void;
}

// The inline slot editor, with the one-helper-per-period guard wired in. Shared
// by the week and month grids so the picker, the dedup, and the assign/remove
// behaviour stay identical across both.
export function SlotEditorPortal({ editing, staff, shifts, holidays, onAssign, onRemove, onClose }: SlotEditorPortalProps) {
  if (!editing) return null;
  const date = editing.date;
  // Helpers on leave that day — flagged (not blocked) in the picker (AC7).
  const onLeaveStaffIds = new Set(staff.filter((s) => isOnLeave(holidays, s.id, date)).map((s) => s.id));
  return (
    <SlotInlineEditor
      staff={staff}
      date={editing.date}
      slot={editing.slot}
      currentStaffId={editing.currentStaffId}
      disabledStaffIds={staffAssignedInPeriod(shifts, editing.date, periodOf(editing.slot), editing.slot)}
      onLeaveStaffIds={onLeaveStaffIds}
      anchorRect={editing.rect}
      onAssign={(staffId) => {
        onAssign(editing.date, editing.slot, staffId);
        onClose();
      }}
      onRemove={() => {
        onRemove(editing.date, editing.slot);
        onClose();
      }}
      onClose={onClose}
    />
  );
}
